// RoomCard — one serialised room in the building capture room list.
// Rendered inside FloorSection and keyed by serial_number.
//
// Visual: 14px radius, white surface with a soft hairline, room name
// + meta line, and a chevron on the right. Status is conveyed by the
// textual "X/Y assets" meta line (suppressed entirely when the room
// has no template, so a fresh building doesn't render a sea of
// "0/0"s) and the data-status attribute exposed for tests.
//
// PRD §7.2.2, buildspec 20-ui §5.4b. Tap target ≥ 44×44 px.

import { ChevronRight, Pencil, Trash2 } from 'lucide-react'
import type { ReactElement } from 'react'

import type { SpaceWithItems } from '@citycare/shared'

export interface RoomCardProps {
  space: SpaceWithItems
  onClick?: () => void
  /**
   * When provided, renders a per-room rename affordance (FFR2 — field edit
   * of a room alias). Same owner + in-progress gating as `onDelete`; the
   * handler opens the rename dialog (it does NOT edit directly).
   */
  onEditAlias?: () => void
  /**
   * When provided, renders a per-room delete affordance (ADR-015 — field
   * delete of rooms). Omitted on read-only / non-owner renders so the
   * control only appears for the capture session owner on an in-progress
   * building. The handler opens the confirmation dialog; it does NOT
   * delete directly.
   */
  onDelete?: () => void
}

type RoomStatus = 'not_started' | 'in_progress' | 'complete'

// Shared navigation-surface classes. Applied to the plain `<button>` in
// the read-only branch and to the full-bleed overlay `<button>` in the
// actions branch, so the card looks and navigates identically either way.
const NAV_SURFACE_CLASSES =
  'rounded-2xl border border-[color:var(--color-cc-forest)]/10 bg-white shadow-sm transition-colors hover:border-[color:var(--color-cc-forest)]/25 hover:bg-[color:var(--color-cc-cream)]/30 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)]'

// Small inline action glyph (pencil / delete). No tile: no background,
// border, or shadow — just a muted glyph that colours on hover. The
// primary ≥44×44 px tap targets (Invariant 10) are the full-row nav
// button (the overlay) plus these 44px-TALL icon buttons (min-h-11); the
// two secondary action icons are intentionally NARROWER than 44px (w-7,
// ≈28px) so the pencil + delete read as a tight, natural pair beside the
// name rather than two widely-spaced boxes — a deliberate, owner-approved
// trade-off. The negative margins keep the 44px hit area from inflating
// the title row's height. `pointer-events-auto` re-enables hit-testing
// inside the pointer-events-none content layer, and `relative z-10` keeps
// the glyph above the overlay nav button.
const INLINE_ACTION_BASE_CLASSES =
  'pointer-events-auto relative z-10 -my-2 flex min-h-11 w-7 flex-shrink-0 items-center justify-center rounded-md text-[color:var(--color-cc-charcoal)]/45 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)]'

export function RoomCard({
  space,
  onClick,
  onEditAlias,
  onDelete,
}: RoomCardProps): ReactElement {
  const total = space.items.length
  // Done = an item where the captured quantity is positive OR the
  // user has explicitly confirmed zero (the two completion
  // signals in CSV export §6.6).
  const done = space.items.filter(
    (item) => parseFloat(item.quantity) > 0 || item.isZeroConfirmed,
  ).length
  const status = computeStatus(done, total)

  const handleClick = () => {
    onClick?.()
  }

  const ariaLabel = makeAriaLabel(space.displayName, done, total)

  // Read-only / non-owner render: no rename or delete affordance, so the
  // whole card is a single navigation `<button>` carrying the name, the
  // optional alias line, the meta line, and the chevron. Markup unchanged
  // from the original design.
  if (onEditAlias === undefined && onDelete === undefined) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={`flex min-h-14 w-full items-center gap-3 px-3.5 py-3 text-left ${NAV_SURFACE_CLASSES}`}
        data-testid={`room-card-${space.serialNumber}`}
        data-status={status}
        aria-label={ariaLabel}
      >
        <RoomCardBody space={space} done={done} total={total} />
      </button>
    )
  }

  // Owner / in-progress render: the rename + delete controls live inline,
  // immediately after the room name in the title row, as small low-contrast
  // glyphs (no tile). They can't be nested inside the nav `<button>` (no
  // button-in-button), so the nav button becomes a full-bleed overlay
  // (absolute inset-0, z-0) and the visible content sits in a
  // pointer-events-none layer above it (z-10). Clicks on the name / meta /
  // chevron pass THROUGH to the overlay (navigation), while the action
  // buttons re-enable pointer events on themselves (pointer-events-auto)
  // and — being siblings of the overlay rather than descendants — never
  // bubble their click to navigation, so they invoke onEditAlias / onDelete
  // and not onClick, with no stopPropagation needed.
  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleClick}
        className={`absolute inset-0 z-0 h-full w-full ${NAV_SURFACE_CLASSES}`}
        data-testid={`room-card-${space.serialNumber}`}
        data-status={status}
        aria-label={ariaLabel}
      />
      <div className="pointer-events-none relative z-10 flex min-h-14 w-full items-center gap-3 px-3.5 py-3 text-left">
        <span className="flex min-w-0 flex-1 flex-col">
          {/* Title row: room name, then a tight little group holding the
               small inline rename / delete glyphs. The group keeps a
               modest gap (gap-1.5, ≈6px) from the name; inside it the two
               glyphs sit close together (gap-0.5, ≈2px) so the pencil +
               delete read as a deliberate pair rather than two separated
               boxes. */}
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate font-display text-[15px] font-semibold text-[color:var(--color-cc-forest)]">
              {space.displayName}
            </span>
            <span className="flex flex-shrink-0 items-center gap-0.5">
              {onEditAlias !== undefined ? (
                <button
                  type="button"
                  onClick={onEditAlias}
                  className={`${INLINE_ACTION_BASE_CLASSES} hover:text-[color:var(--color-cc-forest)]`}
                  data-testid={`room-card-edit-alias-${space.serialNumber}`}
                  aria-label={`Rename ${space.displayName}`}
                >
                  <Pencil aria-hidden="true" className="h-4 w-4" />
                </button>
              ) : null}
              {onDelete !== undefined ? (
                <button
                  type="button"
                  onClick={onDelete}
                  className={`${INLINE_ACTION_BASE_CLASSES} hover:text-[color:var(--color-cc-orange)]`}
                  data-testid={`room-card-delete-${space.serialNumber}`}
                  aria-label={`Delete ${space.displayName}`}
                >
                  <Trash2 aria-hidden="true" className="h-4 w-4" />
                </button>
              ) : null}
            </span>
          </span>
          {/* Optional surveyor-supplied alias (FFR2). Shown as a secondary,
               muted line UNDER the canonical displayName — the generated
               name stays the primary identity (and the aria-label), the
               alias is supplementary context. Rendered only when non-empty.
               Internal/navigation-only; never surfaced in the CSV export. */}
          {hasAlias(space.alias) ? (
            <span className="truncate text-[13px] text-[color:var(--color-cc-charcoal)]/55">
              {space.alias}
            </span>
          ) : null}
          {/* Meta line: room type + asset count. Skipped when the
               template is empty so a never-templated room reads cleanly
               instead of showing a confusing "0/0 assets". */}
          <span className="mt-0.5 truncate text-[12px] text-[color:var(--color-cc-charcoal)]/60">
            {space.roomTypeName}
            {total > 0 ? (
              <>
                <span className="mx-1.5 opacity-40">•</span>
                {done}/{total} assets
              </>
            ) : null}
          </span>
        </span>
        <ChevronRight
          aria-hidden="true"
          className="h-4 w-4 flex-shrink-0 text-[color:var(--color-cc-charcoal)]/40"
        />
      </div>
    </div>
  )
}

// Inner content for the read-only nav button: name, optional alias line,
// meta line, and chevron. The actions branch inlines its own variant of
// this (with the pencil / delete glyphs alongside the name) so it can host
// the sibling action buttons in the title row.
function RoomCardBody({
  space,
  done,
  total,
}: {
  space: SpaceWithItems
  done: number
  total: number
}): ReactElement {
  return (
    <>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-display text-[15px] font-semibold text-[color:var(--color-cc-forest)]">
          {space.displayName}
        </span>
        {/* Optional surveyor-supplied alias (FFR2). Shown as a secondary,
             muted line UNDER the canonical displayName — the generated
             name stays the primary identity (and the aria-label), the
             alias is supplementary context. Rendered only when non-empty.
             Internal/navigation-only; never surfaced in the CSV export. */}
        {hasAlias(space.alias) ? (
          <span className="truncate text-[13px] text-[color:var(--color-cc-charcoal)]/55">
            {space.alias}
          </span>
        ) : null}
        {/* Meta line: room type + asset count. Skipped when the
             template is empty so a never-templated room reads cleanly
             instead of showing a confusing "0/0 assets". */}
        <span className="mt-0.5 truncate text-[12px] text-[color:var(--color-cc-charcoal)]/60">
          {space.roomTypeName}
          {total > 0 ? (
            <>
              <span className="mx-1.5 opacity-40">•</span>
              {done}/{total} assets
            </>
          ) : null}
        </span>
      </span>
      <ChevronRight
        aria-hidden="true"
        className="h-4 w-4 flex-shrink-0 text-[color:var(--color-cc-charcoal)]/40"
      />
    </>
  )
}

// True when the space carries a non-blank alias worth rendering. The
// backend normalises blank/whitespace → null, but this guards a stale
// or odd local row too, so a "  " never renders an empty secondary line.
function hasAlias(alias: string | null): alias is string {
  return alias !== null && alias.trim().length > 0
}

function computeStatus(done: number, total: number): RoomStatus {
  if (total === 0) return 'not_started'
  if (done >= total) return 'complete'
  if (done > 0) return 'in_progress'
  return 'not_started'
}

function makeAriaLabel(
  displayName: string,
  done: number,
  total: number,
): string {
  if (total === 0) return displayName
  return `${displayName}, ${done} of ${total} assets`
}
